import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/UI/Button';
import Input from '../../../components/UI/Input';
import Icon from '../../../components/AppIcon';
import SocialLinks from './SocialLinks';
import { ContactFormData, ContactInfo, SocialLink, PersonalInfo } from '../types';

interface ContactSectionProps {
  contactInfo: ContactInfo;
  socialLinks: SocialLink[];
  personalInfo: PersonalInfo;
}

const ContactSection = ({ contactInfo, socialLinks, personalInfo }: ContactSectionProps) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section className="py-24 relative bg-transparent" id="contact">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Executive Contact Info */}
          <div className="space-y-12">
            <div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-1.5 rounded-full bg-teal-900/5 text-[#0C2C39]/60 text-[10px] font-black uppercase tracking-[0.3em] mb-6"
              >
                {personalInfo.contactBadge || "Engagement Gateway"}
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="font-roboto text-4xl sm:text-5xl lg:text-6xl font-black text-[#0C2C39] mb-6 tracking-tighter leading-[1.1]"
              >
                {personalInfo.contactTitle || "Let's initiate collaboration"}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-lg text-slate-500 font-medium leading-relaxed max-w-xl"
              >
                {personalInfo.contactSubtitle || "I am currently evaluating strategic opportunities and complex engineering mandates."}
              </motion.p>
            </div>

            {/* Verification Channels Grid */}
            <div className="grid gap-6">
              <div className="flex items-center space-x-6 p-6 rounded-[1.5rem] bg-white border border-teal-900/5 hover:border-teal-900/20 hover:shadow-xl transition-all duration-500">
                <div className="w-14 h-14 bg-[#FFF9EB] text-teal-600 rounded-2xl flex items-center justify-center border border-teal-900/5">
                  <Icon name="Mail" size={24} />
                </div>
                <div>
                  <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Direct Correspondence</div>
                  <a href={`mailto:${contactInfo.email}`} className="text-[#0C2C39] font-black text-lg hover:text-teal-600 transition-colors tracking-tight">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-6 p-6 rounded-[1.5rem] bg-white border border-teal-900/5 hover:border-teal-900/20 hover:shadow-xl transition-all duration-500">
                <div className="w-14 h-14 bg-[#FFF9EB] text-teal-600 rounded-2xl flex items-center justify-center border border-teal-900/5">
                  <Icon name="MapPin" size={24} />
                </div>
                <div>
                  <div className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Operational Nexus</div>
                  <div className="text-[#0C2C39] font-black text-lg tracking-tight">{contactInfo.location}</div>
                </div>
              </div>
            </div>

            {/* Strategic Presence */}
            <div>
              <h4 className="text-[9px] font-black uppercase tracking-[0.3em] text-[#0C2C39]/40 mb-8 pb-4 border-b border-teal-900/5">Strategic Digital Presence</h4>
              <SocialLinks socialLinks={socialLinks} />
            </div>
          </div>

          {/* Rapid Transmission Form */}
          <div className="bg-white p-12 lg:p-14 rounded-[3.5rem] shadow-2xl border border-teal-900/5 relative overflow-hidden group">
            {/* Minimal Decorative Bloom */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-teal-900/[0.03] blur-[100px] rounded-full pointer-events-none group-hover:bg-teal-900/[0.05] transition-all duration-700" />

            <h3 className="font-roboto text-3xl font-black text-[#0C2C39] mb-10 tracking-tighter">Secure Transmission</h3>

            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-10 p-5 bg-teal-50 border border-teal-200 rounded-2xl text-teal-700 text-sm font-bold flex items-center shadow-sm"
              >
                <Icon name="CheckCircle" size={20} className="mr-3 text-teal-600" />
                Signal successfully delivered.
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
              <div className="space-y-3">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Principal Identifier</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Portfolio Director or Lead Engineer"
                  required
                  className="bg-slate-50 border-teal-900/5 focus:border-teal-600/30 text-[#0C2C39] placeholder:text-slate-300 rounded-2xl py-4 flex items-center"
                />
              </div>

              <div className="space-y-3">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Communication Protocol</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="nexus@organization.com"
                  required
                  className="bg-slate-50 border-teal-900/5 focus:border-teal-600/30 text-[#0C2C39] placeholder:text-slate-300 rounded-2xl py-4 flex items-center"
                />
              </div>

              <div className="space-y-3">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Mission Briefing</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-5 py-4 bg-slate-50 border border-teal-900/5 rounded-2xl focus:ring-4 focus:ring-teal-600/5 focus:border-teal-600/30 transition-all outline-none text-[#0C2C39] placeholder:text-slate-300 resize-none font-medium text-sm leading-relaxed"
                  placeholder="Synthesize your objective or request a technical consultation..."
                  required
                />
              </div>

              <Button
                type="submit"
                loading={isSubmitting}
                className="btn-primary w-full py-6 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] shadow-2xl hover:brightness-110 active:scale-[0.98] transition-all duration-300"
              >
                {isSubmitting ? 'Transmitting Data...' : 'Dispatch Protocol'}
                {!isSubmitting && <Icon name="Send" size={18} className="ml-3 opacity-60 group-hover:opacity-100 transition-opacity" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;